/**
 * Public certificate lookup page.
 *
 * Phase: 3
 *
 * Minimal-text design: logo, two inputs, submit button. No marketing copy,
 * no admin link, no descriptions. Errors collapse to a one-line message.
 *
 * Backend lookup key is the event's admin-assigned serial_number — see
 * api/app/services/lookup.py for the resolve-by-serial-then-find-cert flow.
 */

import {
  Box,
  Button,
  Card,
  Container,
  Stack,
  Image,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "../../api/client";
import { lookup } from "../../api/public";
import logoSrc from "./assets/logo.png";

const logoSize = 144;

export function Lookup() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm({
    initialValues: { name: "", serialNumber: "" },
    validate: {
      name: (v) => (v.trim().length < 2 ? " " : null),
      serialNumber: (v) => (v.trim().length < 1 ? " " : null),
    },
  });

  const mutation = useMutation({
    mutationFn: lookup,
    onSuccess: (data) => {
      setErrorMessage(null);
      window.location.assign(data.redirect_url);
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof ApiError && err.status === 404
          ? "Sertifika bulunamadı."
          : "Bir hata oluştu.",
      );
    },
  });

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background:
          "linear-gradient(180deg, var(--mantine-color-gray-0) 0%, var(--mantine-color-gray-1) 100%)",
      }}
    >
      <Container size={380} w="100%">
        <Stack gap="xl" align="center">
          <Stack gap={8} align="center">
            <Image
              src={logoSrc}
              alt="maskder logo"
              w={logoSize}
              h={logoSize}
              fit="contain"
              style={{ display: "block" }}
            />
            <Text fw={700} size="xl" c="dark.6">
              MASKDER SERTİFİKA SORGULAMA
            </Text>
          </Stack>

          <Card withBorder shadow="sm" w="100%" p="lg" radius="md">
            <form
              onSubmit={form.onSubmit((values) =>
                mutation.mutate({
                  name: values.name.trim(),
                  serialNumber: values.serialNumber.trim(),
                }),
              )}
            >
              <Stack gap="md">
                <TextInput
                  label="Ad Soyad"
                  size="md"
                  autoFocus
                  {...form.getInputProps("name")}
                />
                <TextInput
                  label="Seri Numarası"
                  size="md"
                  {...form.getInputProps("serialNumber")}
                />

                {errorMessage && (
                  <Text size="sm" c="red" ta="center">
                    {errorMessage}
                  </Text>
                )}

                <Button
                  type="submit"
                  size="md"
                  fullWidth
                  loading={mutation.isPending}
                  mt={errorMessage ? 0 : "xs"}
                >
                  Sertifikamı Göster
                </Button>
              </Stack>
            </form>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
